import React, { useState, useEffect, useRef } from 'react';
import ClinicCard from '../components/ClinicCard';
import SearchInput from '../components/SearchInput';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import { updateOpenGraphTags } from '../utils/opengraph';
import { formatCityDisplay } from '../utils/helpers';

const HomePage = ({
  navigateToHome, navigateToBlog, navigateToB2B, navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap,
  publicSettings, backendUrl,
  searchFilters, setSearchFilters,
  municipalities, showMunicipalityDropdown, setShowMunicipalityDropdown,
  budapestDistricts, districts, services, counties,
  handleAdvancedSearch, showAdvancedSearch, setShowAdvancedSearch,
  showServicesPanel, setShowServicesPanel, servicesRef, nativeInputRef,
  featuredClinics, partners, blogPosts,
  onViewClinic, onViewBlogPost, toggleService,
  isMobileMenuOpen, setIsMobileMenuOpen,
  handleCitySelect, isGA4Loaded, trackEvent
}) => {
  // Set OpenGraph meta tags for homepage
  useEffect(() => {
    updateOpenGraphTags({
      title: 'FogPortál.hu - Magyarország prémium fogászati portálja',
      description: 'Találja meg a legjobb fogorvost Magyarországon! Több mint 1000 fogászati klinika egy helyen.',
      type: 'website',
      url: window.location.origin,
      imageAlt: 'FogPortál.hu - Fogorvos kereső portál'
    }, publicSettings.default_og_image);
  }, [publicSettings.default_og_image]);

  return (
  <div className="min-h-screen bg-gray-50">
    <SEOHead 
      title="Találja meg a legjobb fogorvost – FogPortál.hu országos kereső"
      description="Fogászat kereső és fogorvos kereső országos lefedettséggel. Részletes adatok, szakterületek, nyitvatartás – minden, ami segít a választásban."
      keywords="fogorvos, fogászat, fogászati klinika, fogász, Budapest, Magyarország, fogászati szolgáltatások"
      canonicalUrl={`${window.location.origin}/`}
      schema={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "FogPortál.hu",
        "url": window.location.origin,
        "description": "Fogászat kereső és fogorvos kereső országos lefedettséggel.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${window.location.origin}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        },
        "inLanguage": "hu-HU"
      }}
    />
    {/* Modern Header */}
    <header className="bg-white shadow-lg border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={navigateToHome}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1C7.6 1 4 4.6 4 9c0 2.2.9 4.2 2.3 5.7C7.1 15.4 8 16.7 8 18c0 .6.4 1 1 1s1-.4 1-1c0-1.8-1.1-3.4-2.8-4.1C6 12.1 5.5 10.6 5.5 9c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5c0 1.6-.5 3.1-1.7 4.9C15 15.6 14 17.2 14 18c0 .6.4 1 1 1s1-.4 1-1c0-1.3.9-2.6 1.7-3.3C19.1 13.2 20 11.2 20 9c0-4.4-3.6-8-8-8zm0 4c-2.2 0-4 1.8-4 4 0 1.1.4 2.1 1.2 2.8.5.4.8 1 .8 1.6 0 .6-.4 1-1 1s-1-.4-1-1c0-.4-.2-.7-.5-.9C6.2 11.7 6 10.9 6 10c0-3.3 2.7-6 6-6s6 2.7 6 6c0 .9-.2 1.7-1.5 2.5-.3.2-.5.5-.5.9 0 .6-.4 1-1 1s-1-.4-1-1c0-.6.3-1.2.8-1.6.8-.7 1.2-1.7 1.2-2.8 0-2.2-1.8-4-4-4z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                FogPortál.hu
              </h1>
            </button>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <button 
              onClick={navigateToHome}
              className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg font-medium text-sm hover:bg-blue-100 transition-all duration-200 shadow-sm"
            >
              Főoldal
            </button>
            {/* TEMPORARILY HIDDEN - Blog Menu Item */}
            {/* <button 
              onClick={navigateToBlog}
              className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
            >
              Blog
            </button> */}
            <button 
              onClick={navigateToB2B}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2"
            >
              Megjelenés fogászatoknak
            </button>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2 pt-4 px-4">
              <button 
                onClick={() => {
                  navigateToHome();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Főoldal
              </button>
              <button 
                onClick={() => {
                  navigateToBlog();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-blue-600 bg-blue-50 rounded-lg font-medium hover:bg-blue-100 transition-all duration-200 shadow-sm"
              >
                Blog
              </button>
              <button 
                onClick={() => {
                  navigateToB2B();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Megjelenés fogászatoknak
              </button>
            </div>
          </div>
        )}
      </div>
    </header>

    {/* Hero Section */}
    <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Találja meg a legjobb fogorvost Magyarországon
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-blue-100">
            Több mint 1000 fogászati klinika egy helyen. Könnyen, gyorsan, megbízhatóan.
          </p>
          
          {/* Basic Search Box */}
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-6xl mx-auto">
            {/* Basic Search Row */}
            <div className="flex flex-wrap gap-3 mb-4">
              {/* City Autocomplete - New Hook-Based SearchInput */}
              <SearchInput
                placeholder="Város keresése..."
                onCitySelect={handleCitySelect}
                backendUrl={backendUrl}
              />
              
              {/* County Selector */}
              <div className="flex-1 min-w-[180px]">
                <select 
                  value={searchFilters.county}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, county: e.target.value, city: '', district: '' }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                >
                  <option value="">vagy válasszon vármegyét</option>
                  {counties.map(county => (
                    <option key={county} value={county}>{county}</option>
                  ))}
                </select>
              </div>
              
              {/* District Selector - Only shows for Budapest */}
              {searchFilters.city && searchFilters.city.toLowerCase().includes('budapest') && (
                <div className="flex-1 min-w-[160px]">
                  <select 
                    value={searchFilters.district}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, district: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                  >
                    <option value="">Válasszon kerületet</option>
                    {budapestDistricts.map(district => (
                      <option key={district.id} value={district.name}>{district.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Search Buttons */}
              <div className="flex gap-2 flex-shrink-0">
                <button 
                  onClick={handleAdvancedSearch}
                  className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Keresés
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium flex items-center whitespace-nowrap"
                >
                  <svg className={`w-5 h-5 transform transition-transform ${showAdvancedSearch ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="ml-2">Részletes keresés</span>
                </button>
              </div>
            </div>
            
            {/* Advanced Search Panel - Collapsible */}
            {showAdvancedSearch && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Free Text Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Általános keresés</label>
                    <input 
                      type="text"
                      value={searchFilters.freeText}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, freeText: e.target.value }))}
                      placeholder="Keresés leírásban, címben..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                    />
                  </div>
                  
                  {/* Name Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Klinika neve</label>
                    <input 
                      type="text"
                      value={searchFilters.nameSearch}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, nameSearch: e.target.value }))}
                      placeholder="Keresés klinika nevében..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                    />
                  </div>
                  
                  {/* Services Panel */}
                  <div ref={servicesRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Szolgáltatások</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowServicesPanel(!showServicesPanel)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 text-left flex justify-between items-center"
                      >
                        <span>
                          {searchFilters.services.length === 0 
                            ? "Válasszon szolgáltatásokat" 
                            : `${searchFilters.services.length} szolgáltatás kiválasztva`
                          }
                        </span>
                        <svg className={`w-5 h-5 transform transition-transform ${showServicesPanel ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showServicesPanel && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {services.map(service => (
                            <label key={service} className="flex items-center px-4 py-2 hover:bg-blue-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={searchFilters.services.includes(service)}
                                onChange={() => toggleService(service)}
                                className="mr-3 rounded text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-gray-700">{service}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Additional Options */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={searchFilters.openNow}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, openNow: e.target.checked }))}
                      className="mr-2 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 font-medium">Most nyitva</span>
                  </label>
                  
                  <button
                    onClick={() => {
                      setSearchFilters({
                        city: '',
                        county: '',
                        district: '',
                        services: [],
                        freeText: '',
                        nameSearch: '',
                        openNow: false
                      });
                      setShowAdvancedSearch(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                  >
                    Szűrők törlése
                  </button>
                </div>
              </div>
            )}
            
            {/* Selected Filters Display */}
            {(
              (searchFilters.city && searchFilters.city.length >= 3 && !showMunicipalityDropdown) || 
              searchFilters.county || 
              searchFilters.services.length > 0 || 
              searchFilters.freeText || 
              searchFilters.nameSearch
            ) && (
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 mt-4">
                {searchFilters.city && searchFilters.city.length >= 3 && !showMunicipalityDropdown && (
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {searchFilters.city}
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, city: '', district: '' }))}
                      className="ml-2 hover:text-blue-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchFilters.county && (
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {searchFilters.county}
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, county: '' }))}
                      className="ml-2 hover:text-green-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchFilters.services.map(service => (
                  <span key={service} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    {service}
                    <button 
                      onClick={() => toggleService(service)}
                      className="ml-2 hover:text-purple-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {searchFilters.freeText && (
                  <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    "{searchFilters.freeText}"
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, freeText: '' }))}
                      className="ml-2 hover:text-orange-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchFilters.nameSearch && (
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                    Név: "{searchFilters.nameSearch}"
                    <button 
                      onClick={() => setSearchFilters(prev => ({ ...prev, nameSearch: '' }))}
                      className="ml-2 hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>

    {/* Featured Clinics */}
    {featuredClinics.length > 0 && (
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Kiemelt fogászatok</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredClinics.map(clinic => (
              <div key={clinic.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1 flex flex-col h-full">
                {clinic.logo && (
                  <div className="h-48 bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
                    <img src={`${backendUrl}${clinic.logo}`} alt={clinic.name} className="max-h-full max-w-full object-contain" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">{clinic.name}</h3>
                  <p className="text-gray-600 mb-2 font-medium">{formatCityDisplay(clinic.city, clinic.district)}</p>
                  <p className="text-gray-500 mb-4">{clinic.phone}</p>
                  <div className="mb-6 flex-grow">
                    <div className="flex flex-wrap gap-2">
                      {clinic.services.slice(0, 3).map((service, index) => (
                        <span key={index} className="bg-green-100 text-green-700 px-2 py-1 rounded-lg text-xs font-medium">
                          {service}
                        </span>
                      ))}
                      {clinic.services.length > 3 && (
                        <span className="text-gray-500 text-xs font-medium">+{clinic.services.length - 3} további</span>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => onViewClinic(clinic.id)}
                    className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-semibold mt-auto"
                  >
                    Részletek megtekintése →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )}

    {/* Partners */}
    {partners.length > 0 && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-12">Partnereink</h2>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-6 items-center justify-items-center">
            {partners.map(partner => (
              <div key={partner.id} className="flex justify-center w-full">
                <div className="transition-all duration-300 hover:scale-110 p-3 bg-white rounded-lg shadow-sm hover:shadow-md w-full max-w-32 aspect-square flex flex-col items-center justify-center group relative">
                  <img 
                    src={`${backendUrl}${partner.logo}`} 
                    alt={partner.name}
                    className="max-h-16 max-w-full object-contain mb-2"
                  />
                  
                  {/* Social Media Icons - appear on hover */}
                  {(partner.facebook_url || partner.instagram_url || partner.linkedin_url || partner.website) && (
                    <div className="absolute inset-0 bg-white bg-opacity-95 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center p-2">
                      <div className="text-xs font-semibold text-gray-800 mb-2 text-center">{partner.name}</div>
                      <div className="flex flex-wrap justify-center gap-1">
                        {partner.website && (
                          <a 
                            href={partner.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-6 h-6 bg-blue-600 text-white rounded flex items-center justify-center hover:bg-blue-700 transition-colors"
                            title="Weboldal"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.148.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                            </svg>
                          </a>
                        )}
                        {partner.facebook_url && (
                          <a 
                            href={partner.facebook_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-6 h-6 bg-blue-600 text-white rounded flex items-center justify-center hover:bg-blue-700 transition-colors"
                            title="Facebook"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clipRule="evenodd" />
                            </svg>
                          </a>
                        )}
                        {partner.instagram_url && (
                          <a 
                            href={partner.instagram_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-6 h-6 bg-gradient-to-r from-pink-500 to-yellow-500 text-white rounded flex items-center justify-center hover:from-pink-600 hover:to-yellow-600 transition-all"
                            title="Instagram"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 0C4.478 0 0 4.478 0 10s4.478 10 10 10 10-4.478 10-10S15.522 0 10 0zm4.2 6.4c.4 0 .8.4.8.8v.8c0 .4-.4.8-.8.8h-.8c-.4 0-.8-.4-.8-.8v-.8c0-.4.4-.8.8-.8h.8zM10 6c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4zm0 1.5c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5zM10 2c2.2 0 2.5 0 3.4.1.8 0 1.2.2 1.5.3.4.1.6.3.9.6.3.3.5.5.6.9.1.3.2.7.3 1.5.1.9.1 1.2.1 3.4s0 2.5-.1 3.4c0 .8-.2 1.2-.3 1.5-.1.4-.3.6-.6.9-.3.3-.5.5-.9.6-.3.1-.7.2-1.5.3-.9.1-1.2.1-3.4.1s-2.5 0-3.4-.1c-.8 0-1.2-.2-1.5-.3-.4-.1-.6-.3-.9-.6-.3-.3-.5-.5-.6-.9-.1-.3-.2-.7-.3-1.5C2 12.5 2 12.2 2 10s0-2.5.1-3.4c0-.8.2-1.2.3-1.5.1-.4.3-.6.6-.9.3-.3.5-.5.9-.6.3-.1.7-.2 1.5-.3C7.5 2 7.8 2 10 2z" clipRule="evenodd" />
                            </svg>
                          </a>
                        )}
                        {partner.linkedin_url && (
                          <a 
                            href={partner.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-6 h-6 bg-blue-700 text-white rounded flex items-center justify-center hover:bg-blue-800 transition-colors"
                            title="LinkedIn"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )}

    {/* Recent Blog Posts */}
    {/* TEMPORARILY HIDDEN - Blog Section */}
    {/* {blogPosts.length > 0 && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Legfrissebb cikkeink</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map(post => (
              <div 
                key={post.id} 
                onClick={() => onViewBlogPost(post.id, post.slug)}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1 cursor-pointer transform"
              >
                {post.featured_image ? (
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={`${backendUrl}${post.featured_image}`} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-700 transition-colors">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600">Blogcikk</p>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">{post.title}</h3>
                  <p className="text-gray-600 line-clamp-3">{post.excerpt}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button 
              onClick={navigateToBlog}
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-4 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-semibold text-lg"
            >
              Összes cikk megtekintése
            </button>
          </div>
        </div>
      </section>
    )} */}

    {/* B2B Call to Action Section */}
    <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-2xl p-12 md:p-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Legyen ott, ahol a páciensei keresik Önt
            </h2>
            <h3 className="text-2xl font-semibold text-blue-600 mb-8">
              Miért érdemes csatlakozni?
            </h3>
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              A FogPortál.hu Magyarország országos fogászati portálja, ahol a páciensek egyszerűen, 
              gyorsan és hatékonyan találják meg az Ön rendelőjét. A portál célja, hogy összekösse a 
              pácienseket a legjobb fogászatokkal – az Ön praxisa is ott lehet a következő keresésben.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">Mit nyújtunk?</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Saját, teljes értékű aloldal</p>
                    <p className="text-gray-600">Fogászata akár egyedi, professzionális bemutatkozó oldalt kaphat (fogportal.hu/az-on-rendelője), ami önálló weboldalként működik.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Jobb láthatóság</p>
                    <p className="text-gray-600">A páciensek város és szolgáltatás szerint kereshetnek – így mindig a megfelelő célcsoport találja meg Önt.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">SEO előnyök</p>
                    <p className="text-gray-600">Minden aloldal és a teljes portál keresőoptimalizált, így könnyebben érhető el a Google-ből is.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Kiemelt megjelenés lehetősége</p>
                    <p className="text-gray-600">Extra láthatóság a főoldalon.</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">További előnyök</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">PR megjelenések a blogban</p>
                    <p className="text-gray-600">Ha szeretné, szakmai cikkben is bemutathatja rendelőjét vagy szolgáltatásait.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Havi több tízezer elérés</p>
                    <p className="text-gray-600">A portál látogatottsága havonta több tízezres nagyságrendben mozog, így Ön hirdetési költség nélkül érhet el új pácienseket.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                <h5 className="text-xl font-bold text-gray-900 mb-4">Miért jobb, mint egy hirdetés?</h5>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    <span>A portálon célzottan azok keresnek, akik már most fogorvost keresnek.</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    <span>Egy hagyományos hirdetés gyorsan eltűnik, míg a FogPortálon stabil, folyamatos jelenlétet biztosítunk.</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    <span>Nincs felesleges költés: csak azok látják rendelőjét, akik valóban érdeklődnek.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h4 className="text-2xl font-bold text-gray-900 mb-8">Jelenjen meg még ma!</h4>
            <button 
              onClick={() => window.open('/megjelenes-fogaszatoknak', '_blank')}
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-12 py-4 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-bold text-xl"
            >
              Kattintson a részletekért
            </button>
          </div>
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold text-blue-400 mb-4">FogPortál.hu</h3>
            <p className="text-gray-300 mb-6 max-w-md">
              Magyarország országos fogászati portálja. Találja meg a legjobb fogorvosokat 
              és fogászati klinikákat egyszerűen, gyorsan és hatékonyan.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Gyors linkek</h4>
            <ul className="space-y-2">
              <li><button onClick={navigateToHome} className="text-gray-300 hover:text-blue-400 transition-colors">Főoldal</button></li>
              {/* TEMPORARILY HIDDEN - Blog Menu Item */}
              {/* <li><button onClick={navigateToBlog} className="text-gray-300 hover:text-blue-400 transition-colors">Blog</button></li> */}
              <li><button onClick={navigateToB2B} className="text-gray-300 hover:text-blue-400 transition-colors">Fogorvosoknak</button></li>
              <li><button onClick={navigateToSitemap} className="text-gray-300 hover:text-blue-400 transition-colors">Oldaltérkép</button></li>
            </ul>
          </div>

          {/* Legal and Support */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Információk</h4>
            <ul className="space-y-2">
              <li><button onClick={navigateToPrivacy} className="text-gray-300 hover:text-blue-400 transition-colors">Adatkezelési tájékoztató</button></li>
              <li><button onClick={navigateToTerms} className="text-gray-300 hover:text-blue-400 transition-colors">ÁSZF</button></li>
              <li><a href="/megjelenes-fogaszatoknak" className="text-gray-300 hover:text-blue-400 transition-colors">Kapcsolat</a></li>
              <li><button onClick={navigateToAbout} className="text-gray-300 hover:text-blue-400 transition-colors">Rólunk</button></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 FogPortál.hu. Minden jog fenntartva.
            </p>
            <p className="text-gray-400 text-sm mt-4 md:mt-0">
              Készítette: <span className="text-blue-400">FogPortál Csapat</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  </div>
  );
};


export default HomePage;
